import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import Loader from '@/components/Loader';
import { OneLineText } from '@/components/OneLineText';
import {
  ApplicationErrorFilter,
  ApplicationErrorListItem,
  ApplicationErrorSeverity,
  useGetApplicationErrorById,
  useGetApplicationErrors,
  useGetApplicationErrorSummary,
} from '@/services/applicationErrorService';
import { convertISODateToMMDDYYYY, convertISOToTime } from '@/lib/utils';
import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  RefreshCcw,
  Search,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

const severityOptions: { label: string; value: ApplicationErrorFilter }[] = [
  { label: 'All', value: 'ALL' },
  { label: 'CRITICAL', value: 'CRITICAL' },
  { label: 'ERROR', value: 'ERROR' },
  { label: 'WARN', value: 'WARN' },
  { label: 'INFO', value: 'INFO' },
];

const severityBadgeClasses: Record<ApplicationErrorSeverity, string> = {
  CRITICAL: 'bg-[#FDEBEB] text-error hover:bg-[#FDEBEB]',
  ERROR: 'bg-[#FFE7D6] text-[#B45309] hover:bg-[#FFE7D6]',
  WARN: 'bg-[#FFEEBF] text-[#896010] hover:bg-[#FFEEBF]',
  INFO: 'bg-[#D5E6FB] text-[#1551B2] hover:bg-[#D5E6FB]',
};

const formatValue = (value: unknown) => {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value, null, 2);
};

const ApplicationErrorField = ({ label, value }: { label: string; value: unknown }) => (
  <div className="grid grid-cols-1 gap-1 sm:grid-cols-[180px_1fr] sm:gap-4">
    <div className="text-sm font-medium text-black">{label}</div>
    <div className="text-sm text-black whitespace-pre-wrap break-words">
      {typeof value === 'object' && value !== null ? (
        <pre className="max-h-56 overflow-auto rounded-md border bg-[#0F172A] px-4 py-3 font-mono text-xs leading-5 text-[#E2E8F0]">
          {formatValue(value)}
        </pre>
      ) : typeof value === 'string' && value.length > 0 ? (
        value
      ) : (
        <span className="text-placeholder">{formatValue(value)}</span>
      )}
    </div>
  </div>
);

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <section className="rounded-xl border bg-white shadow-sm">
    <div className="border-b px-4 py-3">
      <h3 className="text-sm font-semibold uppercase tracking-wide text-[#334155]">{title}</h3>
    </div>
    <div className="space-y-4 px-4 py-4">{children}</div>
  </section>
);

const ApplicationErrors = () => {
  const [severity, setSeverity] = useState<ApplicationErrorFilter>('ALL');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedErrorId, setSelectedErrorId] = useState<string | null>(null);

  // Debounce the search input so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const listQuery = useGetApplicationErrors(
    useMemo(() => {
      const params: { severity?: ApplicationErrorSeverity; search?: string; pageNo?: number; pageSize?: number } = {
        pageNo,
        pageSize,
      };
      const trimmedSearch = debouncedSearch.trim();

      if (severity !== 'ALL') {
        params.severity = severity;
      }

      if (trimmedSearch.length > 0) {
        params.search = trimmedSearch;
      }

      return params;
    }, [pageNo, pageSize, debouncedSearch, severity])
  );
  const detailQuery = useGetApplicationErrorById(selectedErrorId);
  const summaryQuery = useGetApplicationErrorSummary();

  const applicationErrors = listQuery.data?.data ?? [];
  const totalRowsCount = listQuery.data?.totalRows ?? 0;
  const totalPages = listQuery.data?.totalPages ?? 1;
  const totalLogs = summaryQuery.data?.totalLogs ?? 0;
  const totalCriticalLogs = summaryQuery.data?.criticalLogs ?? 0;
  const totalErrorLogs = summaryQuery.data?.errorLogs ?? 0;
  const totalWarnLogs = summaryQuery.data?.warnLogs ?? 0;

  const selectedError = detailQuery.data;

  const summaryCards = useMemo(
    () => [
      {
        label: 'Total Logs',
        value: totalLogs,
        accent: 'border-[#D9D9D9] bg-[#FAFAFA]',
      },
      {
        label: 'Critical Logs',
        value: totalCriticalLogs,
        accent: 'border-[#E59A9A] bg-[#FFEAEA]',
      },
      {
        label: 'Error Logs',
        value: totalErrorLogs,
        accent: 'border-[#FFD7B8] bg-[#FFFAF4]',
      },
      {
        label: 'Warn Logs',
        value: totalWarnLogs,
        accent: 'border-[#EEE2FF] bg-[#FBF8FF]',
      },
    ],
    [totalCriticalLogs, totalErrorLogs, totalLogs, totalWarnLogs]
  );

  const handleRowClick = (row: ApplicationErrorListItem) => {
    setSelectedErrorId(row.errorId);
  };

  const closeDrawer = () => {
    setSelectedErrorId(null);
  };

  const handleSearchChange = (value: string) => {
    setPageNo(1);
    setSearch(value);
  };

  const handleSeverityChange = (value: string) => {
    setPageNo(1);
    setSeverity(value as ApplicationErrorFilter);
  };

  const handlePageSizeChange = (value: string) => {
    setPageNo(1);
    setPageSize(Number(value));
  };

  useEffect(() => {
    if (pageNo > totalPages) {
      setPageNo(totalPages);
    }
  }, [pageNo, totalPages]);

  const isFetching = listQuery.isFetching;
  const isLoading = listQuery.isLoading || isFetching;
  const isListError = listQuery.isError;
  const isNoResultsState = !applicationErrors.length;

  return (
    <div className="flex w-full flex-col gap-5">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div className="flex flex-col gap-2">
          <h1 className="text-3xl font-semibold text-black">Application Errors</h1>
        </div>

        <div className="text-sm text-placeholder">
          {totalRowsCount} {totalRowsCount === 1 ? 'error' : 'errors'}
        </div>
      </div>

      <div className="flex w-full flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
        <Input
          Icon={Search}
          aria-label="Search application errors"
          placeholder="Search logs, IDs, endpoints, payloads..."
          value={search}
          onChange={(event) => handleSearchChange(event.target.value)}
          className="w-full xl:max-w-[420px]"
        />

        <div className="flex flex-wrap items-center gap-3">
          <label htmlFor="severity-filter" className="text-sm font-medium text-black">
            Severity
          </label>
          <Select value={severity} onValueChange={handleSeverityChange} aria-label="Severity filter">
            <SelectTrigger id="severity-filter" className="w-[220px]">
              <SelectValue placeholder="All" />
            </SelectTrigger>
            <SelectContent>
              {severityOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
        {summaryCards.map((card) => (
          <div
            key={card.label}
            role="group"
            aria-label={card.label}
            className={`rounded-xl border p-4 shadow-sm ${card.accent}`}
          >
            <div className="text-sm font-medium text-[#475569]">{card.label}</div>
            <div className="mt-2 text-3xl font-semibold tracking-tight text-black">{card.value}</div>
          </div>
        ))}
      </div>

      {isListError && !isNoResultsState ? (
        <div className="flex min-h-[320px] items-center justify-center rounded-lg border border-dashed bg-white p-8 text-center">
          <div className="flex max-w-md flex-col items-center gap-4">
            <AlertTriangle className="h-10 w-10 text-error" />
            <div className="space-y-1">
              <h2 className="text-lg font-semibold text-black">Unable to load application errors</h2>
              <p className="text-sm text-placeholder">
                The application errors API request failed. Try again to reload the dashboard.
              </p>
            </div>
            <Button type="button" variant="outline" onClick={() => listQuery.refetch()}>
              <RefreshCcw className="mr-2 h-4 w-4" />
              Retry
            </Button>
          </div>
        </div>
      ) : (
        <div className="w-full overflow-hidden rounded-xl border bg-white shadow-sm [&>table]:table-fixed [&>table]:min-w-full">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[165px]">Created At</TableHead>
                <TableHead className="w-[130px]">Severity</TableHead>
                <TableHead className="w-[14%]">Application Name</TableHead>
                <TableHead className="w-[14%]">Service Name</TableHead>
                <TableHead className="w-[14%]">Error Type</TableHead>
                <TableHead className="w-[34%]">Error Message</TableHead>
                <TableHead className="w-[60px]" aria-label="row affordance" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="h-24 text-center">
                    <Loader isLoading={true}>
                      <span className="text-placeholder">Loading application errors</span>
                    </Loader>
                  </TableCell>
                </TableRow>
              ) : isNoResultsState ? (
                <TableRow>
                  <TableCell colSpan={7} className="h-24 text-center text-placeholder">
                    No Results
                  </TableCell>
                </TableRow>
              ) : (
                applicationErrors.map((row) => (
                  <TableRow
                    key={row.errorId}
                    className={`cursor-pointer transition-colors hover:bg-[#F8FAFC] ${selectedErrorId === row.errorId ? 'bg-[#EDF5FF]' : ''}`}
                    onClick={() => handleRowClick(row)}
                  >
                    <TableCell className="align-top whitespace-nowrap">
                      {convertISODateToMMDDYYYY(row.createdAt)} {convertISOToTime(row.createdAt)}
                    </TableCell>
                    <TableCell className="align-top">
                      <Badge className={severityBadgeClasses[row.severity]}>{row.severity}</Badge>
                    </TableCell>
                    <TableCell className="align-top">
                      <OneLineText className="max-w-[220px]" text={row.applicationName} />
                    </TableCell>
                    <TableCell className="align-top">
                      <OneLineText className="max-w-[220px]" text={row.serviceName} />
                    </TableCell>
                    <TableCell className="align-top">
                      <OneLineText className="max-w-[220px]" text={row.errorType} />
                    </TableCell>
                    <TableCell className="align-top">
                      <OneLineText className="max-w-[360px]" text={row.errorMessage} />
                    </TableCell>
                    <TableCell className="align-top text-right">
                      <ChevronRight className="ml-auto h-4 w-4 text-placeholder" />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {!isListError && totalRowsCount > 0 && (
        <div className="flex flex-col items-start justify-between gap-3 py-2 sm:flex-row sm:items-center">
          <div className="flex items-center gap-2.5">
            <span className="text-xs font-medium pr-1 xs:text-base">Rows per page</span>
            <Select value={pageSize.toString()} onValueChange={handlePageSizeChange} aria-label="Rows per page">
              <SelectTrigger
                className="h-[36px] w-[70px]"
                customArrow={<ChevronRight className="h-4 w-4 rotate-90 text-[#757575]" />}
              >
                <SelectValue defaultValue="10" className="text-black" />
              </SelectTrigger>
              <SelectContent className="text-black">
                <SelectItem value="10">10</SelectItem>
                <SelectItem disabled={totalRowsCount <= 10} value="20">
                  20
                </SelectItem>
                <SelectItem disabled={totalRowsCount <= 20} value="50">
                  50
                </SelectItem>
                <SelectItem disabled={totalRowsCount <= 100} value="100">
                  100
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <span className="pr-3 text-xs font-medium xs:text-base">
              {!applicationErrors.length ? 0 : (pageNo - 1) * pageSize + 1} -{' '}
              {pageSize * pageNo > totalRowsCount ? totalRowsCount : pageSize * pageNo} of {totalRowsCount}
            </span>

            <Button
              type="button"
              variant="outline"
              className="border-[1px] border-[#E4E4E7] text-[#09090B]"
              size="sm"
              onClick={() => setPageNo(1)}
              disabled={pageNo === 1}
              aria-label="First page"
            >
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="outline"
              className="border-[1px] border-[#E4E4E7] text-[#09090B]"
              size="sm"
              onClick={() => setPageNo(pageNo - 1)}
              disabled={pageNo === 1}
              aria-label="Previous page"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>

            <Button
              type="button"
              variant="outline"
              className="border-[1px] border-[#E4E4E7] text-[#09090B]"
              size="sm"
              onClick={() => setPageNo(pageNo + 1)}
              disabled={pageNo === totalPages || applicationErrors.length === 0 || isFetching}
              aria-label="Next page"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="outline"
              className="border-[1px] border-[#E4E4E7] text-[#09090B]"
              size="sm"
              onClick={() => setPageNo(totalPages)}
              disabled={pageNo === totalPages || applicationErrors.length === 0 || isFetching}
              aria-label="Last page"
            >
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      <Sheet modal={false} open={!!selectedErrorId} onOpenChange={(open) => !open && closeDrawer()}>
        <SheetContent
          side="right"
          hideCloseButton={true}
          className="w-[92vw] sm:w-[820px] overflow-y-auto bg-[#F8FAFC] focus:outline-none"
        >
          <Loader isLoading={detailQuery.isLoading || detailQuery.isFetching}>
            {detailQuery.isError ? (
              <div className="flex min-h-[320px] flex-col items-center justify-center gap-4 text-center">
                <AlertTriangle className="h-10 w-10 text-error" />
                <div className="space-y-1">
                  <h2 className="text-lg font-semibold text-black">Unable to load error details</h2>
                  <p className="text-sm text-placeholder">
                    The selected application error details request failed. Try again to load the panel.
                  </p>
                </div>
                <Button type="button" variant="outline" onClick={() => detailQuery.refetch()}>
                  <RefreshCcw className="mr-2 h-4 w-4" />
                  Retry
                </Button>
              </div>
            ) : (
              <div className="flex flex-col gap-5 pb-6">
                <SheetHeader className="rounded-2xl border bg-white px-4 py-4 shadow-sm">
                  <SheetTitle className="w-full">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex flex-col gap-1">
                        <span className="text-xl font-semibold text-black">Application Error Details</span>
                        <span className="text-sm text-placeholder">{selectedError?.errorId}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {selectedError?.severity && (
                          <Badge className={severityBadgeClasses[selectedError.severity]}>
                            {selectedError.severity}
                          </Badge>
                        )}
                        <button
                          type="button"
                          aria-label="Close error details"
                          className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-[#D0D7DE] bg-white text-[#0A2240] transition-colors hover:bg-[#EDF5FF]"
                          onClick={closeDrawer}
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </SheetTitle>
                </SheetHeader>

                <Section title="Summary">
                  <ApplicationErrorField label="Application Name" value={selectedError?.applicationName} />
                  <ApplicationErrorField label="Service Name" value={selectedError?.serviceName} />
                  <ApplicationErrorField label="Environment" value={selectedError?.environment} />
                  <ApplicationErrorField label="Severity" value={selectedError?.severity} />
                  <ApplicationErrorField label="Error Type" value={selectedError?.errorType} />
                  <ApplicationErrorField label="Error Message" value={selectedError?.errorMessage} />
                  <ApplicationErrorField label="Root Cause" value={selectedError?.rootCause} />
                  <ApplicationErrorField
                    label="Created At"
                    value={
                      selectedError?.createdAt
                        ? `${convertISODateToMMDDYYYY(selectedError.createdAt)} ${convertISOToTime(selectedError.createdAt)}`
                        : '-'
                    }
                  />
                </Section>

                <Section title="Request Context">
                  <ApplicationErrorField label="Request ID" value={selectedError?.requestId} />
                  <ApplicationErrorField label="Correlation ID" value={selectedError?.correlationId} />
                  <ApplicationErrorField label="User ID" value={selectedError?.userId} />
                  <ApplicationErrorField label="HTTP Method" value={selectedError?.httpMethod} />
                  <ApplicationErrorField label="Endpoint" value={selectedError?.endpoint} />
                  <ApplicationErrorField label="HTTP Status Code" value={selectedError?.httpStatusCode} />
                </Section>

                <Section title="Debug Data">
                  <ApplicationErrorField label="Stack Trace" value={selectedError?.stackTrace} />
                  <ApplicationErrorField label="Payload" value={selectedError?.payload} />
                  <ApplicationErrorField label="Metadata" value={selectedError?.metadata} />
                </Section>
              </div>
            )}
          </Loader>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default ApplicationErrors;
